import { useState } from "react";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { interpolateTemplate } from "lib/i18n/dictionary";

import { assistantName } from "./site-assistant";
import { SiteConfirmDialog } from "./site-confirm-dialog";
import { useSiteEntitlements } from "./site.hooks";
import { useOpenClaude, useSiteAssistantConnection } from "./use-site-assistant-connection";
import { useSiteTranslations } from "./use-site-translations";

export function SiteCardIcon({ name }: { name: string }) {
  return (
    <SoftBox
      width="2.5rem"
      height="2.5rem"
      flexShrink={0}
      display="flex"
      alignItems="center"
      justifyContent="center"
      borderRadius="lg"
      variant="gradient"
      bgColor="info"
      color="white"
      shadow="md"
    >
      <Icon fontSize="small">{name}</Icon>
    </SoftBox>
  );
}

// In place of "Підключити Claude" on a Plan without the own assistant (provider_site_own_assistant).
export function OwnAssistantLockedCard() {
  const { t } = useSiteTranslations();
  return (
    <Card data-testid="provider-site-own-assistant-locked">
      <SoftBox p={3} display="flex" gap={2} alignItems="flex-start">
        <SiteCardIcon name="lock" />
        <SoftBox>
          <SoftTypography variant="h6">{t.assistant.locked.title}</SoftTypography>
          <SoftTypography variant="button" color="text" fontWeight="regular">
            {t.assistant.locked.body}
          </SoftTypography>
        </SoftBox>
      </SoftBox>
    </Card>
  );
}

// "Відключити {client}?", asked before the Provider's own Claude loses access.
export function DisconnectAssistantDialog({
  client,
  open,
  busy,
  onConfirm,
  onCancel,
}: {
  client: string;
  open: boolean;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useSiteTranslations();
  return (
    <SiteConfirmDialog
      open={open}
      title={interpolateTemplate(t.assistant.disconnectConfirmTitle, { client })}
      message={interpolateTemplate(t.assistant.disconnectConfirmMessage, { client })}
      confirmLabel={t.assistant.disconnect}
      cancelLabel={t.cancel}
      destructive
      busy={busy}
      onConfirm={onConfirm}
      onCancel={onCancel}
      testId="provider-site-assistant-disconnect-dialog"
    />
  );
}

// The Provider's own Claude on a site that exists. `primary`: without the AI designer, Claude is
// how the Provider changes the site.
export function SiteAssistantCard({ siteHost, primary }: { siteHost: string; primary: boolean }) {
  const { t, intlLocale } = useSiteTranslations();
  const { connection, onConnect, isCreating, onDisconnect, isDisconnecting } =
    useSiteAssistantConnection();
  const openClaude = useOpenClaude();
  const [confirming, setConfirming] = useState(false);
  const { isLoading: isEntitlementsLoading, ownAssistant } = useSiteEntitlements();

  if (!isEntitlementsLoading && !ownAssistant) {
    return <OwnAssistantLockedCard />;
  }

  if (!connection) {
    return (
      <Card data-testid="provider-site-assistant">
        <SoftBox p={3} display="flex" flexDirection="column" gap={2}>
          <SoftBox display="flex" gap={2} alignItems="flex-start">
            <SiteCardIcon name={primary ? "auto_awesome" : "link"} />
            <SoftBox>
              <SoftTypography variant="h6">
                {primary ? t.assistant.changeTitle : t.assistant.title}
              </SoftTypography>
              <SoftTypography variant="button" color="text" fontWeight="regular">
                {primary ? t.assistant.changeBody : t.assistant.body}
              </SoftTypography>
            </SoftBox>
          </SoftBox>
          <SoftButton
            variant={primary ? "gradient" : "outlined"}
            color="info"
            fullWidth
            disabled={isCreating}
            onClick={() => void onConnect()}
            data-testid="provider-site-assistant-connect"
          >
            {isCreating ? <CircularProgress size={16} color="inherit" /> : t.assistant.connect}
          </SoftButton>
        </SoftBox>
      </Card>
    );
  }

  const client = assistantName(connection.clientName);
  const lastUsed = connection.lastUsedAt
    ? interpolateTemplate(t.assistant.lastUsed, {
        date: new Date(connection.lastUsedAt).toLocaleString(intlLocale),
      })
    : t.assistant.neverUsed;

  return (
    <Card data-testid="provider-site-assistant-connected">
      <SoftBox p={3} display="flex" flexDirection="column" gap={2}>
        <SoftBox display="flex" gap={2} alignItems="flex-start">
          <SiteCardIcon name="auto_awesome" />
          <SoftBox>
            <SoftTypography variant="h6">
              {primary
                ? t.assistant.changeTitle
                : interpolateTemplate(t.assistant.connected, { client })}
            </SoftTypography>
            <SoftTypography variant="button" color="text" fontWeight="regular">
              {primary
                ? `${interpolateTemplate(t.assistant.connected, { client })} · ${lastUsed}`
                : lastUsed}
            </SoftTypography>
          </SoftBox>
        </SoftBox>
        <SoftButton
          variant={primary ? "gradient" : "outlined"}
          color="info"
          fullWidth
          onClick={() => openClaude(siteHost)}
          data-testid="provider-site-change-with-claude"
        >
          <Icon sx={{ mr: 1 }}>auto_awesome</Icon>
          {t.assistant.changeWithClaude}
        </SoftButton>
        <SoftButton
          variant="text"
          color="secondary"
          disabled={isDisconnecting}
          onClick={() => setConfirming(true)}
          data-testid="provider-site-assistant-disconnect"
        >
          {primary
            ? interpolateTemplate(t.assistant.disconnectClient, { client })
            : t.assistant.disconnect}
        </SoftButton>
      </SoftBox>
      <DisconnectAssistantDialog
        client={client}
        open={confirming}
        busy={isDisconnecting}
        onConfirm={() => void onDisconnect(client).finally(() => setConfirming(false))}
        onCancel={() => setConfirming(false)}
      />
    </Card>
  );
}
